import environment from '../../.configs/app/environment'

import { buildSentryOptions } from './sentry-replay-sampling.js';
import {
    readStoredConsent,
    CONSENT_STATE,
} from './consent-storage.js';

/** `VITE_SENTRY_ENVIRONMENT_NAME` of a local checkout — never reported. */
const UNREPORTED_SENTRY_ENVIRONMENT = 'workstation';

/**
 * Initializes Sentry error tracking and monitoring
 *
 * Required Environment Variables:
 * - VITE_SENTRY_DSN: The Sentry Data Source Name (DSN) URL for the project
 * - VITE_SENTRY_ENVIRONMENT_NAME: Environment name (e.g., 'production', 'staging', 'development')
 *
 * Sentry is imported dynamically so its (large) bundle is code-split out of the
 * boot-critical entry chunk. Error monitoring isn't needed for first paint, so
 * loading it lazily speeds up initial render on marketing pages.
 *
 * @returns {Promise<void>}
 */
export const initSentry = async () => {

    if (!environment.sentry.dsn) {
        console.warn('Sentry DSN not found');
        return;
    }

    // A developer's local errors (fake ids, fake Stripe accounts, a stopped API) were most
    // of the project's events. Give the env another name to test Sentry locally.
    if (environment.sentry.environment === UNREPORTED_SENTRY_ENVIRONMENT) {
        return;
    }

    // TODO(sentry-replay-rollout): re-enable prod-only gate before merge to prod
    // if (!environment.app.isProd) return  // skip replay integration outside prod

    // Dynamic import keeps the (large) Sentry SDK out of the boot-critical entry
    // chunk; error monitoring isn't needed for first paint, so loading it lazily
    // speeds up initial render on marketing pages.
    let Sentry;
    try {
        Sentry = await import('@sentry/solid');

        const options = buildSentryOptions({
            dsn: environment.sentry.dsn,
            environment: environment.sentry.environment,
        }, Sentry);

        Sentry.init(options)
    } catch (loadError) {
        // Never let an SDK load/init failure (network, CSP, chunk error) break the
        // app. Leave __lodgrSentryReady false so captureError keeps buffering and a
        // later init attempt could still flush. Buffered errors are intentionally
        // retained (not dropped) for that reason.
        console.warn('Sentry failed to load/initialize:', loadError);
        return;
    }

    window.Sentry = Sentry;
    window.__lodgrSentryReady = true;

    // Flush any errors that were thrown before Sentry finished loading.
    const buffered = window.__lodgrEarlyErrors || [];
    window.__lodgrEarlyErrors = [];
    for (const error of buffered) {
        try {
            Sentry.captureException(error);
        } catch { /* never let error reporting throw */ }
    }

    // Session Replay is OPT-IN. The replay integration auto-starts buffering at
    // init because replaysOnErrorSampleRate is 1.0. Apply the persisted consent
    // as a single, mutually-exclusive decision: keep the auto-started buffer
    // running when the user previously accepted, otherwise stop it. Crucially we
    // never call stop() and startBuffering() in the same init — stop() is async,
    // so calling it before a synchronous startBuffering() races and can leave
    // replay disabled on a full page load even when consent is 'accepted' (the
    // original cause of replay silently dropping after navigation). Unknown/
    // declined users record nothing until they Accept on the banner.
    if (readStoredConsent() === CONSENT_STATE.ACCEPTED) {
        Sentry.getReplay()?.startBuffering();
    } else {
        Sentry.getReplay()?.stop();
    }

}

/**
 * Start buffer-mode replay recording. Error-triggered capture + the
 * `beforeErrorSampling` dedup hook apply because replaysOnErrorSampleRate is
 * 1.0. Called when the user accepts the Session Replay consent banner.
 */
export const enableSentryReplay = () => {
    const r = window.Sentry?.getReplay?.();
    r?.startBuffering?.();
};

/**
 * Stop replay recording and end the current session. `stop()` is async; the
 * promise is returned so callers may await it, but it is safe to ignore.
 * Called on decline/revoke and defensively on boot when consent is not granted.
 */
export const disableSentryReplay = () => {
    const r = window.Sentry?.getReplay?.();
    return r?.stop?.();
};


export const captureError = (error) => {
    // Reporting is best-effort and must never throw over the error it is
    // reporting. There is no `window` outside the browser — the prerender
    // build and the unit tests both run this module in bare Node — and a
    // ReferenceError here would replace the real failure with a bogus one.
    if (typeof window === 'undefined') {
        return;
    }
    if (window.Sentry) {
        window.Sentry.captureException(error);
        return;
    }
    // Sentry not loaded yet — queue so initSentry() can flush it once ready.
    // Create the buffer if the inline bootstrap in index.html didn't run (e.g.
    // blocked by CSP, or this module exercised in another context), so early
    // errors aren't silently dropped.
    window.__lodgrEarlyErrors = window.__lodgrEarlyErrors || [];
    if (window.__lodgrEarlyErrors.length < 50) {
        window.__lodgrEarlyErrors.push(error);
    }
}


export const setSentryUser = ({
    id,
    email,
}) => {
    if (!window.Sentry) {return}
    window.Sentry.setUser({
        email,
        id,
    });
}


export const clearSentryUser = () => {
    if (!window.Sentry) {return}
    // eslint-disable-next-line unicorn/no-null -- Sentry unsets the user scope with an explicit null
    window.Sentry.setUser(null);
}
